// src/components/admin/scheduling/schedule_calendar/ScheduleCalendarView.js
//
// Full-screen, vertically-scrollable schedule calendar that replaces the old
// "Schedule Types" settings UI. Visually mirrors the Employee Availability
// calendar (multi-month vertical scroll, sticky weekday header, lazy load
// past/future). Each day cell shows only the resolved start/end time for that
// date (or "Closed") — no type names or descriptions.
//
// Data source: schedule_day_configs (per-date overrides) with a fall-through to
// the editable default weekly schedule. Interactions:
//   • Double-click a day       → edit that day's start/end (single-day modal).
//   • Click a start day, then
//     click an end day          → Weekly Schedule modal applied to the range.
//   • "Set Default"            → edit the default weekly schedule.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  applyWeekConfigToDateRange,
  fetchDayConfigsForDates,
  fetchDefaultScheduleWeekConfig,
  saveDefaultScheduleWeekConfig,
  upsertDayConfigByDate,
} from "../schedulingApi";
import {
  dayKeyFromDate,
  normalizeDaySchedule,
  resolveScheduleForDate,
  setActiveDefaultWeekConfig,
  getActiveDefaultWeekConfig,
} from "../scheduleSettingsUtils";
import {
  addMonths,
  isSameMonth,
  monthGridDays,
  sameYMD,
  startOfMonth,
  ymd,
} from "../../employee/availability/availabilityUtils";
import { formatTimeFromMinutes } from "../timeUtils";
import WeeklyScheduleModal, { DayScheduleModal } from "./WeeklyScheduleModal";

// ── Constants ────────────────────────────────────────────────────────────────
const PAST_INIT = 1;
const FUTURE_INIT = 5;
const LOAD_PAST = 2;
const LOAD_FUTURE = 3;

// Slate palette, matching the availability calendar's "availability" mode card.
const CARD = { bg: "#f1f5f9", border: "#cbd5e1", text: "#334155" };

// ── Helpers ────────────────────────────────────────────────────────────────────
function monthKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function captureAnchor(el) {
  if (!el) return null;
  const cRect = el.getBoundingClientRect();
  const nodes = el.querySelectorAll("[data-month-key]");
  for (const node of nodes) {
    const r = node.getBoundingClientRect();
    if (r.bottom > cRect.top) {
      return { key: node.getAttribute("data-month-key"), top: r.top - cRect.top };
    }
  }
  return null;
}

// ── DayCell ────────────────────────────────────────────────────────────────────
function DayCell({ date, month, idx, today, dayConfigsMap, pendingStart, selectionRange, onClick, onDoubleClick }) {
  const inMonth = isSameMonth(date, month);
  const hasPrevInMonth =
    idx > 0 && isSameMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1), month);
  const hasAboveInMonth = isSameMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7), month);

  if (!inMonth) {
    return <div style={{ height: 100, background: "#fff", pointerEvents: "none" }} />;
  }

  const key = ymd(date);
  const resolved = resolveScheduleForDate(date, dayConfigsMap[key]);
  const isClosed = !!resolved.isClosed;
  const isToday = sameYMD(date, today);
  const isPast = date < today;

  const inRange =
    selectionRange &&
    date.getTime() >= selectionRange.start.getTime() &&
    date.getTime() <= selectionRange.end.getTime();
  const isEndpoint =
    selectionRange && (sameYMD(date, selectionRange.start) || sameYMD(date, selectionRange.end));
  const isArmed = pendingStart && sameYMD(pendingStart, date);
  const isSel = isArmed || inRange;

  // Endpoints / armed start get a ring; interior range days get a lighter fill.
  const cellBg = isArmed || isEndpoint ? "#e0e7ff" : inRange ? "#eef2ff" : isPast ? "#fafafa" : "#fff";

  return (
    <div
      onClick={(e) => onClick(e, date)}
      onDoubleClick={(e) => onDoubleClick(e, date)}
      style={{
        height: 100,
        background: cellBg,
        borderLeft: hasPrevInMonth ? "none" : "1px solid #e5e7eb",
        borderTop: hasAboveInMonth ? "none" : "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        borderRight: "1px solid #e5e7eb",
        padding: "6px 7px 5px",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        cursor: isPast ? "default" : "pointer",
        boxShadow: isArmed || isEndpoint ? "inset 0 0 0 1.5px #4f46e5" : "none",
        opacity: isPast && !isSel ? 0.7 : 1,
        transition: "background-color 0.1s",
        userSelect: "none",
      }}
    >
      {/* Date number */}
      <div style={{ alignSelf: "flex-end", height: 22, display: "flex", alignItems: "center", marginBottom: 4, flexShrink: 0 }}>
        <div
          style={{
            width: isToday ? 22 : "auto",
            height: isToday ? 22 : "auto",
            borderRadius: "50%",
            background: isToday ? "#1f2937" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isToday ? 0 : "0 2px",
          }}
        >
          <span style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? "#fff" : "#374151", lineHeight: 1 }}>
            {date.getDate()}
          </span>
        </div>
      </div>

      {/* Content: closed marker or start/end block */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {isClosed ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#c9cdd4", letterSpacing: "0.3px" }}>Closed</span>
          </div>
        ) : (
          <div
            style={{
              background: CARD.bg,
              border: `1px solid ${CARD.border}`,
              borderRadius: 6,
              padding: "4px 5px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 42,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: CARD.text, lineHeight: 1.3 }}>
              {formatTimeFromMinutes(resolved.startMin)}
            </span>
            <div style={{ width: 18, height: 1, background: CARD.border, margin: "2px 0", opacity: 0.6 }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: CARD.text, lineHeight: 1.3 }}>
              {formatTimeFromMinutes(resolved.endMin)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MonthSection ─────────────────────────────────────────────────────────────
function MonthSection({ month, monthRef, isCurrentMonth, ...cellProps }) {
  const days = useMemo(() => monthGridDays(month), [month]);
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      const week = days.slice(i, i + 7);
      if (week.some((d) => isSameMonth(d, month))) rows.push(week);
    }
    return rows;
  }, [days, month]);

  return (
    <div data-month-key={monthKey(month)} ref={monthRef} style={{ scrollMarginTop: 38 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "26px 0 12px" }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>
          {month.toLocaleDateString("en-US", { month: "long" })}
        </span>
        <span style={{ fontSize: 13, fontWeight: 400, color: "#9ca3af" }}>{month.getFullYear()}</span>
        {isCurrentMonth && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#6b7280",
              border: "1px solid #d1d5db",
              borderRadius: 100,
              padding: "2px 8px",
              lineHeight: 1,
            }}
          >
            This Month
          </span>
        )}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(106px, 1fr))" }}>
          {week.map((date, idx) => (
            <DayCell key={`${ymd(date)}-${idx}`} date={date} month={month} idx={idx} {...cellProps} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ScheduleCalendarView({ open, onClose, onChanged }) {
  const BASE = useMemo(() => startOfMonth(new Date()), []);
  const today = useMemo(() => startOfToday(), []);

  const [months, setMonths] = useState(() =>
    Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, i) => addMonths(BASE, i - PAST_INIT))
  );
  const [pastCount, setPastCount] = useState(PAST_INIT);
  const [dayConfigsMap, setDayConfigsMap] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [defaultWeekConfig, setDefaultWeekConfig] = useState(() => getActiveDefaultWeekConfig());
  const [pendingStart, setPendingStart] = useState(null);
  const pendingStartRef = useRef(null);
  const [selectionRange, setSelectionRange] = useState(null); // { start, end } — persists while range modal is open
  const [rangeModal, setRangeModal] = useState(null); // { start, end }
  const [dayModal, setDayModal] = useState(null); // { date, dayKey, rule }
  const [defaultModalOpen, setDefaultModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const containerRef = useRef(null);
  const currentMonthRef = useRef(null);
  const isLoadingPastRef = useRef(false);
  const isLoadingFutRef = useRef(false);
  const loadedStartRef = useRef(-PAST_INIT);
  const loadedEndRef = useRef(FUTURE_INIT);
  const clickTimerRef = useRef(null);

  const setPending = useCallback((d) => {
    pendingStartRef.current = d;
    setPendingStart(d);
  }, []);

  // ── Data loading ──
  const loadRange = useCallback(async (fromOffset, toOffset) => {
    const rangeMonths = Array.from({ length: toOffset - fromOffset + 1 }, (_, i) => addMonths(BASE, fromOffset + i));
    const allDays = rangeMonths.flatMap((m) => monthGridDays(m));
    return fetchDayConfigsForDates(allDays);
  }, [BASE]);

  // Initial load whenever the view opens.
  useEffect(() => {
    if (!open) return;
    let alive = true;

    const initMonths = Array.from({ length: PAST_INIT + 1 + FUTURE_INIT }, (_, i) => addMonths(BASE, i - PAST_INIT));
    setMonths(initMonths);
    setPastCount(PAST_INIT);
    loadedStartRef.current = -PAST_INIT;
    loadedEndRef.current = FUTURE_INIT;
    setDayConfigsMap({});
    setPending(null);
    setSelectionRange(null);

    (async () => {
      try {
        const active = await fetchDefaultScheduleWeekConfig();
        if (!alive) return;
        setActiveDefaultWeekConfig(active);
        setDefaultWeekConfig(active);
        const cfgs = await loadRange(-PAST_INIT, FUTURE_INIT);
        if (!alive) return;
        setDayConfigsMap(cfgs);
      } catch (e) {
        console.error("Failed to load schedule calendar:", e);
        if (alive) alert("Failed to load schedule calendar.");
      }
    })();

    setTimeout(() => {
      currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 80);

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadMoreFuture = useCallback(async () => {
    if (isLoadingFutRef.current) return;
    isLoadingFutRef.current = true;
    setIsLoadingMore(true);
    const prevEnd = loadedEndRef.current;
    const newEnd = prevEnd + LOAD_FUTURE;
    const newMonths = Array.from({ length: LOAD_FUTURE }, (_, i) => addMonths(BASE, prevEnd + 1 + i));
    try {
      const cfgs = await loadRange(prevEnd + 1, newEnd);
      setDayConfigsMap((p) => ({ ...p, ...cfgs }));
      setMonths((p) => [...p, ...newMonths]);
      loadedEndRef.current = newEnd;
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingFutRef.current = false;
      setIsLoadingMore(false);
    }
  }, [BASE, loadRange]);

  const loadMorePast = useCallback(async () => {
    if (isLoadingPastRef.current) return;
    isLoadingPastRef.current = true;
    const prevStart = loadedStartRef.current;
    const newStart = prevStart - LOAD_PAST;
    const newMonths = Array.from({ length: LOAD_PAST }, (_, i) => addMonths(BASE, newStart + i));
    try {
      const cfgs = await loadRange(newStart, prevStart - 1);
      const el = containerRef.current;
      if (!el) return;
      const anchor = captureAnchor(el);
      flushSync(() => {
        setDayConfigsMap((p) => ({ ...p, ...cfgs }));
        setMonths((p) => [...newMonths, ...p]);
        setPastCount((p) => p + LOAD_PAST);
        loadedStartRef.current = newStart;
      });
      if (anchor?.key) {
        requestAnimationFrame(() => {
          const cRect = el.getBoundingClientRect();
          const anchorNode = el.querySelector(`[data-month-key="${anchor.key}"]`);
          if (!anchorNode) return;
          el.scrollTop += anchorNode.getBoundingClientRect().top - cRect.top - anchor.top;
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingPastRef.current = false;
    }
  }, [BASE, loadRange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !open) return;
    const onScroll = () => {
      if (el.scrollTop < 400) loadMorePast();
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) loadMoreFuture();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [open, loadMorePast, loadMoreFuture]);

  // ── Local day-config updates (keep the grid in sync without a full refetch) ──
  const applyLocalDayConfig = useCallback((date, rule) => {
    const key = ymd(date);
    const normalized = normalizeDaySchedule(rule, rule);
    setDayConfigsMap((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        id: key,
        dateKey: key,
        isClosed: !normalized.isOpen,
        timelineStartMin: normalized.startMin,
        timelineEndMin: normalized.endMin,
      },
    }));
  }, []);

  // ── Interactions ──
  const isEditable = useCallback((date) => date >= today, [today]);

  const handleSingleClick = useCallback(
    (date) => {
      if (!isEditable(date)) {
        setPending(null);
        return;
      }
      const prev = pendingStartRef.current;
      if (!prev) {
        setPending(date);
        return;
      }
      if (sameYMD(prev, date)) {
        setPending(null);
        return;
      }
      const [start, end] = prev.getTime() <= date.getTime() ? [prev, date] : [date, prev];
      setPending(null);
      // Keep the whole range visually selected until the modal is submitted/cancelled.
      setSelectionRange({ start, end });
      setRangeModal({ start, end });
    },
    [isEditable, setPending]
  );

  const handleDoubleClick = useCallback(
    (date) => {
      if (!isEditable(date)) return;
      setPending(null);
      setSelectionRange(null);
      const key = ymd(date);
      const resolved = resolveScheduleForDate(date, dayConfigsMap[key]);
      setDayModal({
        date,
        dayKey: dayKeyFromDate(date),
        rule: { isOpen: !resolved.isClosed, startMin: resolved.startMin, endMin: resolved.endMin },
      });
    },
    [isEditable, dayConfigsMap, setPending]
  );

  const onCellClick = useCallback(
    (e, date) => {
      e.stopPropagation();
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        handleSingleClick(date);
      }, 220);
    },
    [handleSingleClick]
  );

  const onCellDoubleClick = useCallback(
    (e, date) => {
      e.stopPropagation();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      handleDoubleClick(date);
    },
    [handleDoubleClick]
  );

  // ── Persist handlers ──
  const handleDaySave = useCallback(
    async (rule) => {
      if (!dayModal?.date) return;
      setBusy(true);
      try {
        await upsertDayConfigByDate(dayModal.date, {
          isClosed: !rule.isOpen,
          timelineStartMin: rule.startMin,
          timelineEndMin: rule.endMin,
        });
        applyLocalDayConfig(dayModal.date, rule);
        setDayModal(null);
        onChanged?.();
      } catch (e) {
        console.error(e);
        alert("Failed to save day schedule.");
      } finally {
        setBusy(false);
      }
    },
    [dayModal, applyLocalDayConfig, onChanged]
  );

  const handleRangeApply = useCallback(
    async (weekConfig) => {
      if (!rangeModal?.start || !rangeModal?.end) return;
      setBusy(true);
      try {
        await applyWeekConfigToDateRange({
          weekConfig,
          startDate: rangeModal.start,
          endDate: rangeModal.end,
        });
        // Reflect locally for every date in the applied range.
        const cur = new Date(rangeModal.start);
        cur.setHours(0, 0, 0, 0);
        const end = new Date(rangeModal.end);
        end.setHours(0, 0, 0, 0);
        const updates = {};
        while (cur <= end) {
          const key = ymd(cur);
          const rule = normalizeDaySchedule(weekConfig[dayKeyFromDate(cur)], weekConfig[dayKeyFromDate(cur)]);
          updates[key] = {
            id: key,
            dateKey: key,
            isClosed: !rule.isOpen,
            timelineStartMin: rule.startMin,
            timelineEndMin: rule.endMin,
          };
          cur.setDate(cur.getDate() + 1);
        }
        setDayConfigsMap((prev) => ({ ...prev, ...updates }));
        setRangeModal(null);
        setSelectionRange(null);
        onChanged?.();
      } catch (e) {
        console.error(e);
        alert("Failed to apply schedule to the selected range.");
      } finally {
        setBusy(false);
      }
    },
    [rangeModal, onChanged]
  );

  const handleDefaultSave = useCallback(
    async (weekConfig) => {
      setBusy(true);
      try {
        const saved = await saveDefaultScheduleWeekConfig(weekConfig);
        setActiveDefaultWeekConfig(saved);
        setDefaultWeekConfig(saved); // triggers re-render → default cells recompute
        setDefaultModalOpen(false);
        onChanged?.();
      } catch (e) {
        console.error(e);
        alert("Failed to save the default schedule.");
      } finally {
        setBusy(false);
      }
    },
    [onChanged]
  );

  const scrollToToday = () => {
    currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  if (!open) return null;

  const rangeLabel = rangeModal
    ? `${rangeModal.start.toLocaleDateString()} – ${rangeModal.end.toLocaleDateString()}`
    : "";

  return (
    <div style={S.overlay}>
      <div style={S.panel} onClick={() => setPending(null)}>
        {/* Header */}
        <div style={S.header} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button style={S.iconBtn} onClick={onClose} aria-label="Close">✕</button>
            <div>
              <div style={S.title}>Schedule</div>
              <div style={S.hint}>
                Double-click a day to edit · Click a start day, then an end day to apply a weekly schedule
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {pendingStart && (
              <span style={S.pill}>Pick an end day… ({pendingStart.toLocaleDateString()})</span>
            )}
            <button style={S.secondaryBtn} onClick={() => setDefaultModalOpen(true)}>Set Default</button>
            <button style={S.secondaryBtn} onClick={scrollToToday}>Today</button>
          </div>
        </div>

        {/* Calendar */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div ref={containerRef} style={{ height: "100%", overflowY: "auto", overflowX: "auto", background: "#fff" }}>
            {/* Sticky weekday header */}
            <div style={{ position: "sticky", top: 0, zIndex: 10, background: "#fff", borderBottom: "1px solid #e5e7eb", minWidth: 760 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(106px, 1fr))", padding: "0 24px" }}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                  <div
                    key={d}
                    style={{
                      padding: "8px 4px",
                      textAlign: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      color: i === 0 || i === 6 ? "#9ca3af" : "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: "0 24px 80px", minWidth: 760 }}>
              {months.map((month, mi) => (
                <MonthSection
                  key={monthKey(month)}
                  month={month}
                  monthRef={mi === pastCount ? currentMonthRef : null}
                  isCurrentMonth={mi === pastCount}
                  today={today}
                  dayConfigsMap={dayConfigsMap}
                  pendingStart={pendingStart}
                  selectionRange={selectionRange}
                  onClick={onCellClick}
                  onDoubleClick={onCellDoubleClick}
                />
              ))}
              {isLoadingMore && (
                <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#9ca3af" }}>Loading…</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly modal — range apply */}
      <WeeklyScheduleModal
        open={!!rangeModal}
        title="Weekly Schedule"
        subtitle={rangeModal ? `Apply to ${rangeLabel}` : ""}
        confirmLabel="Apply to Range"
        initialWeekConfig={defaultWeekConfig}
        busy={busy}
        onClose={() => { setRangeModal(null); setSelectionRange(null); }}
        onConfirm={handleRangeApply}
      />

      {/* Weekly modal — set default */}
      <WeeklyScheduleModal
        open={defaultModalOpen}
        title="Default Schedule"
        subtitle="Applied to every future date without a specific schedule."
        confirmLabel="Save Default"
        initialWeekConfig={defaultWeekConfig}
        busy={busy}
        onClose={() => setDefaultModalOpen(false)}
        onConfirm={handleDefaultSave}
      />

      {/* Single-day modal */}
      <DayScheduleModal
        open={!!dayModal}
        date={dayModal?.date}
        initialRule={dayModal?.rule}
        busy={busy}
        onClose={() => setDayModal(null)}
        onConfirm={handleDaySave}
      />
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "#fff",
    zIndex: 1100,
    display: "flex",
    flexDirection: "column",
  },
  // Match the Employee Availability calendar: contained, centered, max-width column.
  panel: {
    height: "100%",
    width: "100%",
    maxWidth: "var(--cal-page-max-width, 1440px)",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
  },
  header: {
    flexShrink: 0,
    borderBottom: "1px solid #e5e7eb",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: { fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: "-0.4px", lineHeight: 1.1 },
  hint: { fontSize: 11.5, color: "#9ca3af", marginTop: 3 },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 9,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 14,
    color: "#374151",
    cursor: "pointer",
    flexShrink: 0,
  },
  secondaryBtn: {
    padding: "7px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    cursor: "pointer",
    flexShrink: 0,
  },
  pill: {
    fontSize: 11.5,
    fontWeight: 600,
    color: "#4338ca",
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: 100,
    padding: "5px 11px",
    whiteSpace: "nowrap",
  },
};
